import { Clipboard } from '@angular/cdk/clipboard';
import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Injectable } from '@angular/core';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import dxPopup, { Properties, ToolbarItem } from 'devextreme/ui/popup';
import { AbcPopupDialogComponent } from './abc-popup-dialog.component';
import {
  AbcPopupDialogButtonClass,
  AbcPopupDialogButtonCombination,
  AbcPopupDialogButtonResourceIdentifier,
  AbcPopupDialogCustomAction,
  AbcPopupDialogIconResourceIdentifier,
  AbcPopupDialogSettings,
  AbcPopupDialogTitleResourceIdentifier,
  DialogResult,
} from './abc-popup-dialog.model';

@Injectable({
  providedIn: 'root',
})
export class AbcPopupDialogService {
  private container: HTMLDivElement;
  private componentRef: ComponentRef<AbcPopupDialogComponent>;
  private popupInstance: dxPopup;
  private popupPromiseResolve: (value: DialogResult) => void;

  private resAsync: Promise<ResourceSet>;

  public get visible(): boolean {
    return !!this.popupInstance;
  }

  constructor(
    private resourceManagerService: ResourceManagerService,
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector,
    private clipboard: Clipboard,
  ) {
    this.resAsync = this.resourceManagerService.getResourceSet('abc-popup-dialog');
  }
  public async showInfoDialog(info: string, settings?: AbcPopupDialogSettings): Promise<DialogResult> {
    const defaultsettings: AbcPopupDialogSettings = new AbcPopupDialogSettings();
    defaultsettings.buttonCombination = AbcPopupDialogButtonCombination.Ok;
    defaultsettings.classExtension = 'info';

    defaultsettings.shortMessage = info;

    defaultsettings.headerTitle = (await this.resAsync).get(AbcPopupDialogTitleResourceIdentifier.Info);
    defaultsettings.headerSVGIcon = await (await this.resAsync).svgContent(AbcPopupDialogIconResourceIdentifier.Info);
    defaultsettings.height = settings?.height ?? '300px';
    defaultsettings.width = settings?.width ?? '386px';
    defaultsettings.position = settings?.position;

    return await this.showDialog(defaultsettings);
  }

  public async showQuestionDialog(question: string, settings?: AbcPopupDialogSettings): Promise<DialogResult> {
    const defaultsettings: AbcPopupDialogSettings = new AbcPopupDialogSettings();
    defaultsettings.buttonCombination = settings?.buttonCombination ?? AbcPopupDialogButtonCombination.YesNo;
    defaultsettings.customButtons = settings?.customButtons ?? [];

    defaultsettings.classExtension = 'question';

    defaultsettings.headerTitle =
      settings?.headerTitle ?? (await this.resAsync).get(AbcPopupDialogTitleResourceIdentifier.Question);
    defaultsettings.headerSVGIcon = await (
      await this.resAsync
    ).svgContent(AbcPopupDialogIconResourceIdentifier.Question);

    defaultsettings.shortMessage = question;
    defaultsettings.height = settings?.height ?? '300px';
    defaultsettings.width = settings?.width ?? '386px';
    defaultsettings.position = settings?.position;

    return await this.showDialog(defaultsettings);
  }

  public async showWarningDialog(warning: string, settings?: AbcPopupDialogSettings): Promise<DialogResult> {
    const defaultsettings: AbcPopupDialogSettings = new AbcPopupDialogSettings();
    defaultsettings.buttonCombination = AbcPopupDialogButtonCombination.Ok;

    defaultsettings.classExtension = 'warning';

    defaultsettings.headerTitle = (await this.resAsync).get(AbcPopupDialogTitleResourceIdentifier.Warning);
    defaultsettings.headerSVGIcon = await (
      await this.resAsync
    ).svgContent(AbcPopupDialogIconResourceIdentifier.Warning);

    defaultsettings.shortMessage = warning;
    defaultsettings.height = settings?.height ?? '300px';
    defaultsettings.width = settings?.width ?? '386px';
    defaultsettings.position = settings?.position;
    return await this.showDialog(defaultsettings);
  }

  public async showErrorDialog(
    error: string,
    errorMessage: string,
    settings?: AbcPopupDialogSettings,
  ): Promise<DialogResult> {
    const defaultsettings: AbcPopupDialogSettings = new AbcPopupDialogSettings();
    defaultsettings.buttonCombination = AbcPopupDialogButtonCombination.CopyClose;

    defaultsettings.classExtension = 'error';

    defaultsettings.headerTitle = (await this.resAsync).get(AbcPopupDialogTitleResourceIdentifier.Error);
    defaultsettings.headerSVGIcon = await (await this.resAsync).svgContent(AbcPopupDialogIconResourceIdentifier.Error);

    defaultsettings.shortMessage = error;
    defaultsettings.longMessage = errorMessage;

    if (defaultsettings.longMessage) {
      defaultsettings.resizeEnabled = true;
      defaultsettings.minHeight = 350;
      defaultsettings.minWidth = 350;
      defaultsettings.width = settings?.width ?? '454px';
      defaultsettings.height = settings?.height ?? '424px';
    }

    defaultsettings.position = settings?.position;

    return await this.showDialog(defaultsettings);
  }

  public async showDialog(settings: AbcPopupDialogSettings): Promise<DialogResult> {
    if (this.popupInstance) {
      this.removePopup();
    }

    const popupOptions: Properties = await this.createPopupOptions(settings);
    this.createPopupContainer();

    return new Promise((resolve) => {
      this.popupPromiseResolve = resolve;

      this.popupInstance = new dxPopup(this.container, popupOptions);

      this.popupInstance.show();
    });
  }

  //#region PopupOptions
  private async createPopupOptions(settings: AbcPopupDialogSettings): Promise<Properties> {
    const popupOptions: Properties = {
      width: settings.width,
      maxWidth: settings.maxWidth,
      minWidth: settings.minWidth,
      height: settings.height,
      maxHeight: settings.maxHeight,
      minHeight: settings.minHeight,
      resizeEnabled: !!settings.resizeEnabled,
      showTitle: settings.showTitle,
      showCloseButton: settings.showCloseButton,
      title: settings.title,
      hideOnOutsideClick: settings.hideOnOutsideClick,
      shading: settings.shading,
      shadingColor: settings.shadingColor,
      container: settings.container,
      position: settings.position,
      animation: settings.animation,
      toolbarItems: [],
      wrapperAttr: { class: 'abc-popup-dialog-dx-popup' },
      contentTemplate: (contentElement: HTMLElement) => {
        this.attachComponent(contentElement);

        this.componentRef.instance.settings = settings;
      },
      onHidden: () => {
        this.removePopup();
      },
    };

    await this.createButtons(settings, popupOptions);

    return popupOptions;
  }

  private async createButtons(settings: AbcPopupDialogSettings, popupOptions: Properties) {
    switch (settings.buttonCombination) {
      case AbcPopupDialogButtonCombination.Ok:
      default:
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Ok, DialogResult.Ok),
        );
        break;
      case AbcPopupDialogButtonCombination.OkCancel:
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Ok, DialogResult.Ok),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Cancel, DialogResult.Cancel),
        );
        break;
      case AbcPopupDialogButtonCombination.AbortRetryIgnore:
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Abort, DialogResult.Abort),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Retry, DialogResult.Retry),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Ignore, DialogResult.Ignore),
        );
        break;
      case AbcPopupDialogButtonCombination.YesNoCancel:
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Yes, DialogResult.Yes),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.No, DialogResult.No),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Cancel, DialogResult.Cancel),
        );
        break;
      case AbcPopupDialogButtonCombination.YesIgnoreCancel:
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Yes, DialogResult.Yes),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Ignore, DialogResult.Ignore),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Cancel, DialogResult.Cancel),
        );
        break;
      case AbcPopupDialogButtonCombination.YesNo:
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Yes, DialogResult.Yes),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.No, DialogResult.No),
        );
        break;
      case AbcPopupDialogButtonCombination.RetryCancel:
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Retry, DialogResult.Retry),
        );
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Cancel, DialogResult.Cancel),
        );
        break;
      case AbcPopupDialogButtonCombination.CopyOk:
        popupOptions.toolbarItems.push(await this.createCopyButton(settings));
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Ok, DialogResult.Ok),
        );
        break;
      case AbcPopupDialogButtonCombination.CopyClose:
        popupOptions.toolbarItems.push(await this.createCopyButton(settings));
        popupOptions.toolbarItems.push(
          await this.createButton(AbcPopupDialogButtonResourceIdentifier.Close, DialogResult.Close),
        );
        break;
      case AbcPopupDialogButtonCombination.Custom:
        this.createCustomButtons(popupOptions, settings.customButtons);
        break;
    }
  }

  private createCustomButtons(popupOptions: Properties, customButtons: AbcPopupDialogCustomAction[]) {
    customButtons.forEach(async (customButton: AbcPopupDialogCustomAction) => {
      const button: ToolbarItem = await this.createButtonCore(customButton.text, customButton.dialogResult);

      if (customButton.cssClass) {
        button.cssClass = customButton.cssClass;
      }

      if (customButton.elementAttr) {
        button.options.elementAttr = customButton.elementAttr;
      }

      popupOptions.toolbarItems.push(button);
    });
  }

  private async createCopyButton(settings: AbcPopupDialogSettings): Promise<ToolbarItem> {
    const copyButton: ToolbarItem = await this.createButton(
      AbcPopupDialogButtonResourceIdentifier.Copy,
      DialogResult.None,
    );

    copyButton.options.elementAttr.class = AbcPopupDialogButtonClass.Copy;
    copyButton.options.onClick = () => {
      this.clipboard.copy(settings.longMessage);
    };

    return copyButton;
  }

  private async createButton(buttonTextIdentifier: string, dialogResult: DialogResult): Promise<ToolbarItem> {
    const buttonText: string = (await this.resAsync).get(buttonTextIdentifier);

    return await this.createButtonCore(buttonText, dialogResult);
  }

  private async createButtonCore(buttonText: string, dialogResult: DialogResult): Promise<ToolbarItem> {
    const button: ToolbarItem = {
      widget: 'dxButton',
      location: 'center',
      toolbar: 'bottom',
      cssClass: 'abc-popup-dialog-toolbarItem',
      options: {
        text: buttonText,
        elementAttr: { class: this.getButtonClassName(dialogResult) },
      },
    };

    if (dialogResult !== DialogResult.None) {
      button.options.onClick = () => {
        this.popupPromiseResolve(dialogResult);
        this.popupInstance.hide();
      };
    }

    return button;
  }

  private getButtonClassName(dialogResult: DialogResult): string {
    let className: string;

    switch (dialogResult) {
      case DialogResult.Ok:
      default:
        className = AbcPopupDialogButtonClass.Ok;
        break;
      case DialogResult.Cancel:
        className = AbcPopupDialogButtonClass.Cancel;
        break;
      case DialogResult.Abort:
        className = AbcPopupDialogButtonClass.Abort;
        break;
      case DialogResult.Retry:
        className = AbcPopupDialogButtonClass.Retry;
        break;
      case DialogResult.Ignore:
        className = AbcPopupDialogButtonClass.Ignore;
        break;
      case DialogResult.Yes:
        className = AbcPopupDialogButtonClass.Yes;
        break;
      case DialogResult.No:
        className = AbcPopupDialogButtonClass.No;
        break;
      case DialogResult.Close:
        className = AbcPopupDialogButtonClass.Close;
        break;
      case DialogResult.None:
        className = AbcPopupDialogButtonClass.None;
        break;
    }

    return className;
  }
  //#endregion

  private createPopupContainer(): void {
    this.container = document.createElement('div');
    this.container.id = 'abcPopupContainer';
    document.body.appendChild(this.container);
  }

  private attachComponent(contentElement: HTMLElement): void {
    this.componentRef = createComponent(AbcPopupDialogComponent, {
      environmentInjector: this.environmentInjector,
    });

    this.appRef.attachView(this.componentRef.hostView);
    contentElement.appendChild(this.componentRef.location.nativeElement);
  }

  private removePopup(): void {
    if (this.popupInstance) {
      this.popupInstance.dispose();

      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.container.remove();

      this.popupInstance = null;
      this.componentRef = null;
      this.container = null;
      this.popupPromiseResolve = null;
    }
  }
}
